import type { ReactNode } from 'react'
import type { Bi, ReviewStatus } from '@/sanskrit/lib/sanskrit/types'

import { BadgeCheck, CircleAlert } from 'lucide-react'
import { cn } from '@/sanskrit/lib/utils'
import { STATUS_LABEL } from '@/sanskrit/lib/sanskrit/labels'
import { useT } from '@/sanskrit/components/providers/app-state'

export function T({ text }: { text: Bi | string }) {
    const t = useT()
    return <>{t(text)}</>
}

export function StatusBadge({ status, className }: { status: ReviewStatus; className?: string }) {
    const t = useT()
    const verified = status === 'verified' || status === 'published'

    return (
        <span
            className={cn(
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.7rem] font-semibold',
                verified ? 'bg-tulsi-soft text-tulsi' : 'bg-terracotta-soft text-terracotta',
                className
            )}
            title={verified
                ? t({ hi: 'मानक व्याकरण-ग्रन्थों से मिलाया गया', en: 'Cross-checked against standard reference grammars' })
                : t({ hi: 'विद्वान् की समीक्षा बाकी है; अंक वाले अभ्यास में प्रयोग नहीं होता', en: 'Awaiting scholarly review; never used in graded exercises' })}
        >
            {verified ? <BadgeCheck className='size-3.5' /> : <CircleAlert className='size-3.5' />}
            {t(STATUS_LABEL[status])}
        </span>
    )
}

export function Chip({ children, tone = 'default', className }: { children: ReactNode; tone?: 'default' | 'saffron' | 'terracotta' | 'ink' | 'tulsi'; className?: string }) {
    return (
        <span
            className={cn(
                'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium',
                tone === 'default' && 'bg-muted text-muted-foreground',
                tone === 'saffron' && 'bg-saffron-soft text-saffron-strong',
                tone === 'terracotta' && 'bg-terracotta-soft text-terracotta',
                tone === 'ink' && 'bg-ink-soft text-ink',
                tone === 'tulsi' && 'bg-tulsi-soft text-tulsi',
                className
            )}
        >
            {children}
        </span>
    )
}

export function Fact({ label, sa, value, sub }: { label: Bi | string; sa?: string; value: ReactNode; sub?: ReactNode }) {
    const t = useT()

    return (
        <div className='rounded-xl border border-border bg-background/60 px-3 py-2'>
            <dt className='text-[0.7rem] font-semibold tracking-wide text-muted-foreground uppercase'>
                {sa && <span className='deva mr-1 normal-case tracking-normal'>{sa}</span>}
                {t(label)}
            </dt>
            <dd className='mt-0.5 text-sm font-semibold text-foreground'>{value}</dd>
            {sub && <dd className='text-xs text-muted-foreground'>{sub}</dd>}
        </div>
    )
}

export function SectionHeading({ sa, title, subtitle, className, align = 'left' }: { sa?: string; title: Bi | string; subtitle?: Bi | string; className?: string; align?: 'left' | 'center' }) {
    const t = useT()

    return (
        <div className={cn(align === 'center' && 'text-center', className)}>
            {sa && <p lang='sa' className='deva text-sm font-semibold text-saffron-strong'>{sa}</p>}
            <h2 className='font-heading text-2xl text-foreground sm:text-3xl'>{t(title)}</h2>
            {subtitle && <p className={cn('mt-2 max-w-2xl text-muted-foreground', align === 'center' && 'mx-auto')}>{t(subtitle)}</p>}
        </div>
    )
}

export function PageHeader({ sa, title, subtitle, children, className }: { sa: string; title: Bi | string; subtitle?: Bi | string; children?: ReactNode; className?: string }) {
    const t = useT()

    return (
        <header className={cn('mx-auto max-w-6xl px-4 pt-8 pb-6 sm:px-6 sm:pt-12', className)}>
            <p lang='sa' className='literary text-lg text-saffron-strong'>{sa}</p>
            <h1 className='mt-1 font-heading text-3xl text-foreground sm:text-4xl'>{t(title)}</h1>
            {subtitle && <p className='mt-3 max-w-3xl text-base text-muted-foreground sm:text-lg'>{t(subtitle)}</p>}
            {children}
        </header>
    )
}

export function Segmented<V extends string>({
    value,
    options,
    onChange,
    label,
    className,
    size = 'md'
}: {
    value: V
    options: Array<{ value: V; label: ReactNode; title?: string }>
    onChange: (v: V) => void
    label: string
    className?: string
    size?: 'sm' | 'md'
}) {
    return (
        <div role='radiogroup' aria-label={label} className={cn('inline-flex flex-wrap gap-1 rounded-2xl border border-border bg-card p-1', className)}>
            {options.map(o => (
                <button
                    key={o.value}
                    type='button'
                    role='radio'
                    aria-checked={value === o.value}
                    title={o.title}
                    onClick={() => onChange(o.value)}
                    className={cn(
                        'rounded-xl font-medium transition-colors',
                        size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm',
                        value === o.value ? 'bg-ink text-primary-foreground shadow-sm' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    )}
                >
                    {o.label}
                </button>
            ))}
        </div>
    )
}
